import {
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
} from "@chakra-ui/react";
import { Button } from "components/ds";
import FilterDropdown, { FilterOption } from "components/FilterDropdown";
import { Calendar, Search } from "lucide-react";
import React, { useState } from "react";
import { FiTag } from "react-icons/fi";
import { FILTERS_REGISTRY } from "../filters/filterRegistry";
import { FilterOperator, FilterType } from "./FilterBar";

// Base interface for all filter inputs
interface FilterInputProps {
  onApply: (value: any) => void;
  onCancel: () => void;
  placeholder?: string;
  options?: FilterOption[];
}

// Text filter input component
export const TextFilterInput: React.FC<FilterInputProps> = ({
  onApply,
  onCancel,
  placeholder = "Enter text...",
  options = [],
}) => {
  const [value, setValue] = useState("");
  const hasOptions = options && options.length > 0;
  return (
    <Stack spacing={2}>
      {hasOptions ? (
        <FilterDropdown
          initialOptions={options}
          size="xs"
          value={[]}
          placeholder="Select an option"
          icon={FiTag}
          singleSelect={true}
          createable
          onChange={(vals) => {
            onApply(vals[0]?.value);
          }}
        />
      ) : (
        <InputGroup size="xs">
          <InputLeftElement pointerEvents="none">
            <Icon as={Search} color="gray.400" boxSize={3} />
          </InputLeftElement>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            autoFocus
            borderRadius="sm"
            fontSize="xs"
            height="24px"
            borderColor="gray.200"
            _focus={{
              borderColor: "gray.300",
              boxShadow: "none",
            }}
          />
        </InputGroup>
      )}
      <HStack justify="flex-end">
        <Button size="xs" variant="subtle" tone="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="xs"
          variant="solid"
          tone="primary"
          onClick={() => onApply(value || "")} // Ensure we never send null
          isDisabled={!value.trim()}
        >
          Apply
        </Button>
      </HStack>
    </Stack>
  );
};

// Date filter input component
export const DateFilterInput: React.FC<FilterInputProps> = ({
  onApply,
  onCancel,
  placeholder = "Select date...",
}) => {
  const [value, setValue] = useState("");

  return (
    <Stack spacing={2}>
      <InputGroup size="xs">
        <InputLeftElement pointerEvents="none">
          <Icon as={Calendar} color="gray.400" boxSize={3} />
        </InputLeftElement>
        <Input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          autoFocus
          borderRadius="sm"
          fontSize="xs"
          height="24px"
          borderColor="gray.200"
          _focus={{
            borderColor: "gray.300",
            boxShadow: "none",
          }}
        />
      </InputGroup>
      <HStack justify="flex-end">
        <Button size="xs" variant="subtle" tone="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="xs"
          variant="solid"
          tone="primary"
          onClick={() => onApply(value || "")} // Ensure we never send null
          isDisabled={!value}
        >
          Apply
        </Button>
      </HStack>
    </Stack>
  );
};

// Select filter input component
interface SelectFilterInputProps extends FilterInputProps {
  options: FilterOption[];
  multiple?: boolean;
}

export const SelectFilterInput: React.FC<SelectFilterInputProps> = ({
  onApply,
  onCancel,
  options,
  multiple = false,
}) => {
  const [selectedValues, setSelectedValues] = useState<FilterOption[]>([]);

  const handleSelectChange = (values: FilterOption[]) => {
    setSelectedValues(values);
  };

  return (
    <Stack spacing={2}>
      <FilterDropdown
        initialOptions={options}
        size="xs"
        value={selectedValues}
        placeholder="Select an option"
        icon={FiTag}
        singleSelect={!multiple}
        defaultOpen={true}
        createable
        onChange={handleSelectChange}
      />

      <HStack justify="flex-end">
        <Button size="xs" variant="subtle" tone="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="xs"
          variant="solid"
          tone="primary"
          onClick={() =>
            onApply(
              selectedValues.length > 0
                ? selectedValues.map((v) => v.value)
                : [""],
            )
          } // Ensure we never send empty array
          isDisabled={selectedValues.length === 0}
        >
          Apply
        </Button>
      </HStack>
    </Stack>
  );
};

// Factory function to get the right filter input based on type and operator
export const getFilterInput = (
  type: FilterType,
  operator: FilterOperator,
  onApply: (value: any) => void,
  onCancel: () => void,
  customOptions: FilterOption[] = [],
) => {
  // Special case for operators that don't need value input
  if (operator === "isEmpty" || operator === "isNotEmpty") {
    return null; // No input needed
  }

  const filterConfig = FILTERS_REGISTRY[type];
  if (!filterConfig) return null;

  // First check for custom options, then fall back to registry options
  const options =
    customOptions.length > 0 ? customOptions : filterConfig.options || [];
  const isMultiSelect = operator === "isOneOf";

  if (operator === "contains") {
    return (
      <TextFilterInput
        onApply={onApply}
        onCancel={onCancel}
        placeholder={`Enter ${filterConfig.label.toLowerCase()}...`}
        options={[]}
      />
    );
  }

  if (isMultiSelect || options?.length) {
    return (
      <SelectFilterInput
        onApply={(val) =>
          operator === "equals" && Array.isArray(val)
            ? onApply(val[0])
            : onApply(val)
        }
        onCancel={onCancel}
        options={options}
        multiple={isMultiSelect}
        placeholder={`Select ${filterConfig.label.toLowerCase()}...`}
      />
    );
  }

  switch (filterConfig.valueType) {
    case "date":
      return (
        <DateFilterInput
          onApply={onApply}
          onCancel={onCancel}
          placeholder={`Select date...`}
        />
      );

    case "select":
    case "multiselect":
      return (
        <SelectFilterInput
          onApply={onApply}
          onCancel={onCancel}
          options={options}
          multiple={isMultiSelect}
          placeholder={`Select ${filterConfig.label.toLowerCase()}...`}
        />
      );

    case "text":
    default:
      return (
        <TextFilterInput
          onApply={onApply}
          onCancel={onCancel}
          placeholder={`Enter ${filterConfig.label.toLowerCase()}...`}
          options={options}
        />
      );
  }
};
